import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import {
  AREA_UNIT,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
} from 'constants/uiConstants';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { InputTaskCategory } from 'interfaces/IInputTask';
import {
  computeNutritionPerHa,
  NUTRIENT_DISPLAY_ORDER,
  NUTRIENT_LABELS,
  NUTRIENT_PIE_COLORS,
  type NutritionPerHaResult,
  type NutritionTally,
  type PartPaddockApplication,
} from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import {
  ACTUAL_ONLY_CATEGORIES,
  PLAN_ONLY_CATEGORIES,
  scaleForDisplay,
} from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import NutritionProgressChart, {
  type NutrientProgressItem,
} from './NutritionProgressChart';
import { ChartEmptyState } from './PlanActualChartControls';

/**
 * A nutrient row's shared display unit — Plan and Actual are always shown in
 * the same unit (kg or t) so they stay directly comparable within a row; the
 * unit itself is shown once next to the nutrient's own label rather than
 * repeated on every value cell. Picked from whichever of Plan/Actual is
 * larger, so a row that's crossed the tonne threshold shows both figures in
 * tonnes even if one of them individually wouldn't have.
 */
const resolveRowUnit = (plan: number, actual: number): string =>
  scaleForDisplay(Math.max(plan, actual, 0), 'kg').unit ?? 'kg';

const scaleToUnit = (value: number, unit: string): number =>
  unit === 't' ? value / 1000 : value;

/** One nutrient's rate, formatted for display in the row's shared unit. isOverPlan flags an Actual value that exceeds (or has no) Plan rate — see the chart's own over-plan treatment for the same reasoning. */
const NutrientValue: React.FC<{
  tally: NutritionTally | null;
  nutrientKey: keyof NutritionTally;
  unit: string;
  isOverPlan?: boolean;
}> = ({ tally, nutrientKey, unit, isOverPlan }) => {
  const raw = tally?.[nutrientKey] ?? 0;
  if (raw <= 0) return <span className="text-gray-300">—</span>;
  const value = scaleToUnit(raw, unit);
  return (
    <span className={isOverPlan ? 'font-semibold text-danger' : undefined}>
      {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </span>
  );
};

const formatNumber = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatApplication = (app: PartPaddockApplication): string => {
  const rate = app.rateUnit
    ? `${formatNumber(app.rate)} ${app.rateUnit}/${AREA_UNIT}`
    : formatNumber(app.rate);
  return `${app.productName}: ${rate} on ${formatNumber(app.areaHa)} ${AREA_UNIT}`;
};

/**
 * Explains the area the rates are averaged over (OP-2386). Only rendered when
 * that isn't obvious: a contributing step covered part of the paddock
 * (including variable-rate zones), or the paddock area differs between plans.
 * Plan and Actual share the same plans and divisor, so either result's plans
 * describe both.
 */
const AveragedAreaNote: React.FC<{
  plan: NutritionPerHaResult;
  actual: NutritionPerHaResult;
}> = ({ plan, actual }) => {
  const areasDiffer = plan.plans.some(
    (p) => formatNumber(p.areaHa) !== formatNumber(plan.plans[0].areaHa)
  );
  const groups = [
    { label: 'Plan', apps: plan.partPaddockApplications },
    { label: 'Actual', apps: actual.partPaddockApplications },
  ].filter((g) => g.apps.length > 0);

  if (!areasDiffer && groups.length === 0) return null;

  const title = (
    <div className="flex flex-col gap-1">
      <p>
        {areasDiffer
          ? `Paddock area differs between plans: ${plan.plans
              .map((p) => `${p.label} ${formatNumber(p.areaHa)} ${AREA_UNIT}`)
              .join(', ')}. Each plan's rate uses its own area.`
          : `Averaged over ${formatNumber(plan.areaHa)} ${AREA_UNIT}.`}
        {groups.length > 0 &&
          ' Includes applications to part of the paddock, including variable-rate zones:'}
      </p>
      {groups.map((g) => (
        <div key={g.label}>
          <p className="font-semibold">{g.label}</p>
          <ul className="list-disc pl-4">
            {g.apps.map((app, idx) => (
              <li key={`${app.inputId}-${idx}`}>{formatApplication(app)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip title={title} placement="left">
      <InfoCircleOutlined
        aria-label="How nutrient rates are averaged"
        tabIndex={0}
        className="cursor-help text-gray-muted"
      />
    </Tooltip>
  );
};

/**
 * Displays a compact tally of planned nutrient rates (kg/ha) across all
 * SoilInput steps within the current tree selection scope.
 *
 * Shows Plan and Actual side by side when the panel is wide enough; below
 * RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX it collapses to a single column driven
 * by the shared Plan/Actual control at the top of the Right Panel (OP-2333 —
 * one control for every section, not one per panel). The chart shows both
 * at once per nutrient — Actual applied so far against that nutrient's own
 * Plan target (OP-2333; previously a composition-% donut with a Plan/Actual
 * toggle, see NutritionProgressChart for the current design).
 *
 * Only nutrients with a non-zero rate (in either category) are shown.
 * Rates ≥ 1000 kg/ha are auto-scaled to t/ha using scaleForDisplay.
 */
const NutritionTallyPanel: React.FC = () => {
  const { scopedInputs, isLoading: isInputsLoading } = useScopedInputs();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isWide = width === 0 || width >= RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;
  const narrowCategory = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
  );

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[RIGHT_PANEL_TAB_NUTRITION]
  );
  const { data: gUnits = [], isLoading: isGUnitsLoading } = useFetchGUnits(
    activeGOrganisation || null
  );

  const isLoading = isInputsLoading || isGUnitsLoading;

  const [hoveredNutrient, setHoveredNutrient] = useState<string | null>(null);

  const planResult = useMemo(
    () =>
      isLoading
        ? null
        : computeNutritionPerHa(scopedInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const actualResult = useMemo(
    () =>
      isLoading
        ? null
        : computeNutritionPerHa(scopedInputs, gUnits, ACTUAL_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const planPerHa = planResult?.perHa ?? null;
  const actualPerHa = actualResult?.perHa ?? null;

  if (isLoading) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        <span className="text-base text-gray-muted">Loading...</span>
      </div>
    );
  }

  const activeRows = NUTRIENT_DISPLAY_ORDER.filter(
    (key) => (planPerHa?.[key] ?? 0) > 0 || (actualPerHa?.[key] ?? 0) > 0
  );

  if (activeRows.length === 0) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8 text-center">
        <span className="text-base text-gray-muted">No nutrient data</span>
      </div>
    );
  }

  const narrowTally = narrowCategory === InputTaskCategory.Plan ? planPerHa : actualPerHa;
  const isOverPlan = (key: keyof NutritionTally): boolean => {
    const plan = planPerHa?.[key] ?? 0;
    const actual = actualPerHa?.[key] ?? 0;
    return actual > 0 && (plan <= 0 || actual > plan);
  };

  const progressItems: NutrientProgressItem[] = activeRows.map((key) => ({
    key,
    plan: planPerHa?.[key] ?? 0,
    actual: actualPerHa?.[key] ?? 0,
  }));

  return (
    <div ref={ref} className="flex flex-col gap-4">
      <table className="w-full text-base">
        <thead>
          <tr className="border-b">
            <th className="pb-1.5 pr-3 text-left text-sm font-semibold text-gray-muted">
              <span className="inline-flex items-center gap-1.5">
                Nutrient
                {planResult && actualResult && (
                  <AveragedAreaNote plan={planResult} actual={actualResult} />
                )}
              </span>
            </th>
            {isWide ? (
              <>
                <th className="pb-1.5 text-right text-sm font-semibold text-gray-muted">
                  Plan
                </th>
                <th className="pb-1.5 text-right text-sm font-semibold text-gray-muted">
                  Actual
                </th>
              </>
            ) : (
              <th className="pb-1.5 text-right text-sm font-semibold text-gray-muted">
                {narrowCategory === InputTaskCategory.Plan ? 'Plan' : 'Actual'}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {activeRows.map((key, idx) => {
            const unit = resolveRowUnit(planPerHa?.[key] ?? 0, actualPerHa?.[key] ?? 0);
            return (
              <tr
                key={key}
                onMouseEnter={() => setHoveredNutrient(key)}
                onMouseLeave={() => setHoveredNutrient(null)}
                className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
              >
                <td className="py-1 pr-3 text-label text-charcoal">
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="8" height="8" aria-hidden="true" className="shrink-0">
                      <circle
                        cx="4"
                        cy="4"
                        r="3.5"
                        fill={NUTRIENT_PIE_COLORS[key] ?? '#9ca3af'}
                      />
                    </svg>
                    {NUTRIENT_LABELS[key]}
                    <span className="text-xs font-semibold text-gray-muted">
                      ({unit}/ha)
                    </span>
                  </span>
                </td>
                {isWide ? (
                  <>
                    <td className="py-1 text-right tabular-nums text-charcoal">
                      <NutrientValue tally={planPerHa} nutrientKey={key} unit={unit} />
                    </td>
                    <td className="py-1 text-right tabular-nums text-charcoal">
                      <NutrientValue
                        tally={actualPerHa}
                        nutrientKey={key}
                        unit={unit}
                        isOverPlan={isOverPlan(key)}
                      />
                    </td>
                  </>
                ) : (
                  <td className="py-1 text-right tabular-nums text-charcoal">
                    <NutrientValue
                      tally={narrowTally}
                      nutrientKey={key}
                      unit={unit}
                      isOverPlan={
                        narrowCategory === InputTaskCategory.Actual && isOverPlan(key)
                      }
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {chartVisible &&
        (progressItems.length > 0 ? (
          <NutritionProgressChart
            items={progressItems}
            hoveredKey={hoveredNutrient}
            onHoverKeyChange={setHoveredNutrient}
          />
        ) : (
          <ChartEmptyState />
        ))}
    </div>
  );
};

export default NutritionTallyPanel;
